import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { PersonalService } from '../services/personalService';
import '../styles/pages/personal.css';

const AREAS = ['Recepción', 'Veterinaria', 'Estética', 'Guardería', 'Administración', 'Transporte'];
const CONTRATOS = ['Indefinido', 'Determinado', 'Por obra'];
const ESTATUS_OPTS = [
    { value: 'activo',      label: 'Activo' },
    { value: 'vacaciones',  label: 'Vacaciones' },
    { value: 'incidencia',  label: 'Incidencia' },
    { value: 'incapacidad', label: 'Incapacidad' },
    { value: 'baja',        label: 'Baja' },
];
const DOC_ESTADOS = [
    { value: 'ok',   label: 'Entregado' },
    { value: 'warn', label: 'Pendiente' },
    { value: 'bad',  label: 'Falta' },
];

function toDateInput(iso) {
    if (!iso) return '';
    return iso.slice(0, 10);
}

export default function PersonalEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { authorizedFetch, role } = useAuth();

    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [toast, setToast] = useState('');

    useEffect(() => {
        if (role !== 'admin') { navigate('/settings'); return; }
        PersonalService.getById(id, authorizedFetch)
            .then(emp => setForm(normalizeForm(emp)))
            .catch(() => setError('No se pudo cargar el empleado.'))
            .finally(() => setLoading(false));
    }, [id]); // eslint-disable-line

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(''), 3000);
        return () => clearTimeout(t);
    }, [toast]);

    function normalizeForm(emp) {
        return {
            // Basic
            nombre:       emp.nombre || '',
            puesto:       emp.puesto || '',
            area:         emp.area || '',
            estatus:      emp.estatus || 'activo',
            ingreso:      toDateInput(emp.ingreso),
            tipoContrato: emp.tipoContrato || 'Indefinido',
            // Personal
            curp:         emp.curp || '',
            rfc:          emp.rfc || '',
            nss:          emp.nss || '',
            nacimiento:   emp.nacimiento || '',
            domicilio:    emp.domicilio || '',
            tel:          emp.tel || '',
            email:        emp.email || '',
            // Documents
            ine:           { estado: emp.ine?.estado || 'warn', detalle: emp.ine?.detalle || '' },
            compDomicilio: { estado: emp.compDomicilio?.estado || 'warn', detalle: emp.compDomicilio?.detalle || '' },
            contrato:      { estado: emp.contrato?.estado || 'warn', detalle: emp.contrato?.detalle || '' },
            retenciones:   { estado: emp.retenciones?.estado || 'warn', detalle: emp.retenciones?.detalle || '' },
            // Medical
            sangre:       emp.sangre || '',
            alergias:     emp.alergias || '—',
            cronicas:     emp.cronicas || '—',
            medicamentos: emp.medicamentos || '—',
            clinica:      emp.clinica || '',
            seguro:       emp.seguro || '',
            // Emergency contacts (up to 2)
            emergencia: (emp.emergencia || []).slice(0, 2).map(c => ({
                nombre: c.nombre || '',
                rel:    c.rel || '',
                tel:    c.tel || '',
            })),
            // Vacation
            vacDerecho: emp.vacaciones?.derecho ?? 0,
            vacTomados: emp.vacaciones?.tomados ?? 0,
            // Payroll
            banco:      emp.banco || '',
            cuenta:     emp.cuenta || '',
            clabe:      emp.clabe || '',
            sueldoBase: emp.sueldoBase || '',
        };
    }

    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
    const setDoc = (doc, field, value) => setForm(prev => ({ ...prev, [doc]: { ...prev[doc], [field]: value } }));
    const setContact = (i, field, value) => setForm(prev => {
        const emergencia = [...prev.emergencia];
        if (!emergencia[i]) emergencia[i] = { nombre: '', rel: '', tel: '' };
        emergencia[i] = { ...emergencia[i], [field]: value };
        return { ...prev, emergencia };
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.nombre || !form.puesto || !form.area || !form.ingreso) {
            setError('Nombre, puesto, área y fecha de ingreso son obligatorios.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const payload = {
                ...form,
                vacaciones: { derecho: Number(form.vacDerecho), tomados: Number(form.vacTomados) },
                emergencia: form.emergencia.filter(c => c.nombre.trim()),
            };
            delete payload.vacDerecho;
            delete payload.vacTomados;
            await PersonalService.update(id, payload, authorizedFetch);
            setToast('Cambios guardados');
            setTimeout(() => navigate(`/settings/personal/${id}`), 800);
        } catch (err) {
            setError('No se pudieron guardar los cambios.');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (
        <>
            <Navigation />
            <div className="pe-loading" style={{ paddingTop: 100 }}>Cargando…</div>
        </>
    );

    if (!form) return (
        <>
            <Navigation />
            <div className="pe-container">
                <div className="pe-toast error">{error || 'Empleado no encontrado.'}</div>
            </div>
        </>
    );

    return (
        <>
            <Navigation />
            <div className="pe-container">
                <button className="pe-back-link" onClick={() => navigate(`/settings/personal/${id}`)}>
                    ‹ Expediente
                </button>

                <div className="pe-ph">
                    <div>
                        <h1 className="pe-ph-title">Editar empleado</h1>
                        <p className="pe-ph-sub">{form.nombre}</p>
                    </div>
                </div>

                {toast && <div className="pe-toast success">{toast}</div>}
                {error && <div className="pe-toast error">{error}</div>}

                <form onSubmit={handleSubmit}>

                    {/* ── Datos básicos ─────────────────── */}
                    <div className="pe-form-card">
                        <h3>🪪 Datos personales</h3>
                        <div className="pe-field">
                            <label>Nombre completo *</label>
                            <input value={form.nombre} onChange={e => set('nombre', e.target.value)} />
                        </div>
                        <div className="pe-row2">
                            <div className="pe-field">
                                <label>CURP</label>
                                <input value={form.curp} onChange={e => set('curp', e.target.value)} />
                            </div>
                            <div className="pe-field">
                                <label>RFC</label>
                                <input value={form.rfc} onChange={e => set('rfc', e.target.value)} />
                            </div>
                        </div>
                        <div className="pe-row2">
                            <div className="pe-field">
                                <label>NSS (IMSS)</label>
                                <input value={form.nss} onChange={e => set('nss', e.target.value)} />
                            </div>
                            <div className="pe-field">
                                <label>Fecha de nacimiento</label>
                                <input type="date" value={form.nacimiento} onChange={e => set('nacimiento', e.target.value)} />
                            </div>
                        </div>
                        <div className="pe-field">
                            <label>Domicilio</label>
                            <textarea rows="2" value={form.domicilio} onChange={e => set('domicilio', e.target.value)} />
                        </div>
                    </div>

                    {/* ── Contacto y puesto ─────────────── */}
                    <div className="pe-form-card">
                        <h3>📞 Contacto y puesto</h3>
                        <div className="pe-row2">
                            <div className="pe-field">
                                <label>Teléfono</label>
                                <input value={form.tel} onChange={e => set('tel', e.target.value)} />
                            </div>
                            <div className="pe-field">
                                <label>Correo</label>
                                <input type="email" value={form.email} onChange={e => set('email', e.target.value)} />
                            </div>
                        </div>
                        <div className="pe-row2">
                            <div className="pe-field">
                                <label>Puesto *</label>
                                <input value={form.puesto} onChange={e => set('puesto', e.target.value)} />
                            </div>
                            <div className="pe-field">
                                <label>Área *</label>
                                <select value={form.area} onChange={e => set('area', e.target.value)}>
                                    <option value="">Selecciona…</option>
                                    {AREAS.map(a => <option key={a}>{a}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="pe-row2">
                            <div className="pe-field">
                                <label>Fecha de ingreso *</label>
                                <input type="date" value={form.ingreso} onChange={e => set('ingreso', e.target.value)} />
                            </div>
                            <div className="pe-field">
                                <label>Tipo de contrato</label>
                                <select value={form.tipoContrato} onChange={e => set('tipoContrato', e.target.value)}>
                                    {CONTRATOS.map(c => <option key={c}>{c}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="pe-field">
                            <label>Estatus</label>
                            <select value={form.estatus} onChange={e => set('estatus', e.target.value)}>
                                {ESTATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* ── Documentación legal ───────────── */}
                    <div className="pe-form-card">
                        <h3>📋 Documentación legal</h3>
                        {[
                            { key: 'ine',           icon: '🪪', label: 'Identificación oficial (INE)' },
                            { key: 'compDomicilio',  icon: '🏠', label: 'Comprobante de domicilio' },
                            { key: 'contrato',       icon: '📄', label: 'Contrato de trabajo' },
                            { key: 'retenciones',    icon: '🏦', label: 'Retenciones de crédito' },
                        ].map(doc => (
                            <div key={doc.key} style={{ marginBottom: 14 }}>
                                <label style={{ fontSize: '.78rem', fontWeight: 800, color: '#374151', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                    {doc.icon} {doc.label}
                                </label>
                                <div className="pe-row2">
                                    <div className="pe-field" style={{ marginBottom: 0 }}>
                                        <label>Estado</label>
                                        <select value={form[doc.key].estado} onChange={e => setDoc(doc.key, 'estado', e.target.value)}>
                                            {DOC_ESTADOS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="pe-field" style={{ marginBottom: 0 }}>
                                        <label>Detalle</label>
                                        <input
                                            placeholder="Ej. Vigente · 2027"
                                            value={form[doc.key].detalle}
                                            onChange={e => setDoc(doc.key, 'detalle', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ── Médico ────────────────────────── */}
                    <div className="pe-form-card">
                        <h3>🏥 Datos de salud</h3>
                        <div className="pe-row2">
                            <div className="pe-field">
                                <label>Tipo de sangre</label>
                                <input placeholder="Ej. O+" value={form.sangre} onChange={e => set('sangre', e.target.value)} />
                            </div>
                            <div className="pe-field">
                                <label>Alergias graves</label>
                                <input value={form.alergias} onChange={e => set('alergias', e.target.value)} />
                            </div>
                        </div>
                        <div className="pe-row2">
                            <div className="pe-field">
                                <label>Enf. crónicas</label>
                                <input value={form.cronicas} onChange={e => set('cronicas', e.target.value)} />
                            </div>
                            <div className="pe-field">
                                <label>Medicamentos</label>
                                <input value={form.medicamentos} onChange={e => set('medicamentos', e.target.value)} />
                            </div>
                        </div>
                        <div className="pe-field">
                            <label>Clínica IMSS</label>
                            <input value={form.clinica} onChange={e => set('clinica', e.target.value)} />
                        </div>
                        <div className="pe-field">
                            <label>SGMM particular</label>
                            <input value={form.seguro} onChange={e => set('seguro', e.target.value)} />
                        </div>
                    </div>

                    {/* ── Contactos de emergencia ───────── */}
                    <div className="pe-form-card">
                        <h3>🚨 Contactos de emergencia</h3>
                        {[0, 1].map(i => (
                            <div key={i} style={{ marginBottom: i === 0 ? 14 : 0 }}>
                                <p style={{ margin: '0 0 8px', fontSize: '.78rem', fontWeight: 800, color: '#6b7280' }}>
                                    {i + 1}º contacto
                                </p>
                                <div className="pe-row2">
                                    <div className="pe-field" style={{ marginBottom: 8 }}>
                                        <label>Nombre</label>
                                        <input
                                            value={form.emergencia[i]?.nombre || ''}
                                            onChange={e => setContact(i, 'nombre', e.target.value)}
                                        />
                                    </div>
                                    <div className="pe-field" style={{ marginBottom: 8 }}>
                                        <label>Relación</label>
                                        <input
                                            placeholder="Madre, Esposo…"
                                            value={form.emergencia[i]?.rel || ''}
                                            onChange={e => setContact(i, 'rel', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="pe-field">
                                    <label>Teléfono</label>
                                    <input
                                        value={form.emergencia[i]?.tel || ''}
                                        onChange={e => setContact(i, 'tel', e.target.value)}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ── Vacaciones ────────────────────── */}
                    <div className="pe-form-card">
                        <h3>🏖️ Vacaciones</h3>
                        <div className="pe-row2">
                            <div className="pe-field">
                                <label>Días con derecho</label>
                                <input type="number" min="0" value={form.vacDerecho} onChange={e => set('vacDerecho', e.target.value)} />
                            </div>
                            <div className="pe-field">
                                <label>Días tomados</label>
                                <input type="number" min="0" value={form.vacTomados} onChange={e => set('vacTomados', e.target.value)} />
                            </div>
                        </div>
                    </div>

                    {/* ── Nómina ────────────────────────── */}
                    <div className="pe-form-card">
                        <h3>💳 Datos bancarios y nómina</h3>
                        <div className="pe-row2">
                            <div className="pe-field">
                                <label>Banco</label>
                                <input placeholder="BBVA, Santander…" value={form.banco} onChange={e => set('banco', e.target.value)} />
                            </div>
                            <div className="pe-field">
                                <label>Cuenta</label>
                                <input value={form.cuenta} onChange={e => set('cuenta', e.target.value)} />
                            </div>
                        </div>
                        <div className="pe-field">
                            <label>CLABE interbancaria</label>
                            <input value={form.clabe} onChange={e => set('clabe', e.target.value)} />
                        </div>
                        <div className="pe-field">
                            <label>Sueldo base</label>
                            <input placeholder="Ej. $19,600 / mes" value={form.sueldoBase} onChange={e => set('sueldoBase', e.target.value)} />
                        </div>
                    </div>

                    <div className="pe-btn-row">
                        <button type="button" className="pe-btn pe-btn-ghost" onClick={() => navigate(`/settings/personal/${id}`)}>
                            Cancelar
                        </button>
                        <button type="submit" className="pe-btn pe-btn-pri" disabled={saving}>
                            {saving ? 'Guardando…' : 'Guardar cambios'}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
}
